// @ts-nocheck
// Agent Portal (/cti2) — "My Dashboard" view, shown instead of the live-call
// cards when the agent flips the Topbar's Live-call/My-dashboard switch
// (see AgentPortalPage.tsx's `showDashboard` derivation).
//
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { CallStatsBody } from '@/features/cti/components/AgentCallStatsPanel';
import PeriodCallsScatterModal from '@/features/cti/components/PeriodCallsScatterModal';
import PeriodOverviewRow from '@/features/cti/components/call-stats/PeriodOverviewRow';
import FilterBarPill from '@/features/cti/components/call-stats/filters/FilterBarPill';
import { useCallStatsData } from '@/features/cti/components/call-stats/useCallStatsData';
import { dateToInputStr } from '@/features/cti/components/call-stats/format';
import CallLogTable from '@/features/cti/components/call-stats/CallLogTable';
import LoggedInAgentBreakTable from '@/features/cti/components/call-stats/LoggedInAgentBreakTable';
import VoicemailsPanel from '@/features/dashboard/widgets/VoicemailsPanel';
import LeadsScreen from '@/features/cti/screens/LeadsScreen';
import DispositionFollowUpsPanel from '@/features/dashboard/widgets/DispositionFollowUpsPanel';
import DispositionFormsList from '@/features/disposition-forms/DispositionFormsListPage';
import DashboardSection from '../components/DashboardSection';
import DashboardSectionNav from '../components/DashboardSectionNav';
import AgentGroupsSection from '../components/AgentGroupsSection';
import AgentCampaignsSection from '../components/AgentCampaignsSection';
import { HAIRLINE } from '../team/teamUi';
import { useModuleOps } from '../hooks/useModuleOps';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import { canUseDispositionFollowUpsFeature } from '@/utilities/dispositionFormsAcl';
import { useCallStore } from '@/router/zustand/callStore';

export default function AgentDashboardView({
  userData,
  theme = 'light',
  isOutboundEnabled = false,
  onCallLead,
  onCallFollowUp,
  onCallFromLog = null,
  followUpCallBlockedReason = null,
  onRequestDashboard,
  campaigns = [],
  pendingDispositionsCount = 0,
  active = true,
}) {
  const followUpsRefreshToken = useCallStore((st) => st.followUpsRefreshToken);
  const scrollRootRef = React.useRef(null);
  const navElRef = useRef(null);
  const filterBarRef = useRef(null);
  const stuckHeightRef = useRef(56);
  const isTeamLead = userData?.agentData?.access === 'team_lead';
  const isDark = theme === 'dark';
  const agentId = userData?.agentData?.id;

  // These dashboard sections had no ACL check at all — an admin removing
  // Voicemail/Agent Leads/Disposition Forms from an agent's ACL had no
  // effect here, the section just kept showing regardless.
  const moduleOps = useModuleOps();
  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const isVoicemailEnabled = useMemo(() => {
    const fromProp = userData?.agentData?.is_voicemail_enabled ?? userData?.agent_details?.is_voicemail_enabled;
    if (fromProp !== undefined && fromProp !== null) return Boolean(fromProp);
    try {
      const raw = getUser() || {};
      return Boolean(raw?.agentData?.is_voicemail_enabled ?? raw?.agent_details?.is_voicemail_enabled);
    } catch {
      return false;
    }
  }, [userData]);
  const canReadVoicemail = isVoicemailEnabled
    && (isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.VOICEMAIL, 'read'));
  const canReadLeads = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.AGENT_LEADS, 'read');
  // No separate ACL module exists for Disposition Follow-Ups — it's a
  // Disposition Forms feature, so it's gated by that same module.
  const canReadDispositionForms = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.DISPOSITION_FORMS, 'read');
  const canUseFollowUps = canUseDispositionFollowUpsFeature(moduleOps, { isSuperAdmin });
  const canReadCallGroup = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CALL_GROUP, 'read');
  const canReadCampaigns = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CAMPAIGNS, 'read');

  const csData = useCallStatsData(agentId);
  const [leadsPending, setLeadsPending] = useState(0);
  const [followUpsPending, setFollowUpsPending] = useState(0);
  const [activeSectionId, setActiveSectionId] = useState('call-stats');
  const [leadsStatusFilter, setLeadsStatusFilter] = useState('all');
  const [leadsSearchTerm, setLeadsSearchTerm] = useState('');
  const [followUpsStatus, setFollowUpsStatus] = useState('');
  const [followUpsSearch, setFollowUpsSearch] = useState('');
  const [voicemailsPortalNode, setVoicemailsPortalNode] = useState(null);
  const [leadsPortalNode, setLeadsPortalNode] = useState(null);
  const [followUpsPortalNode, setFollowUpsPortalNode] = useState(null);
  // This view now stays permanently mounted (see AgentPortalPage.tsx), so
  // this effect only ever runs once — if Topbar's own JSX around
  // #ap-topbar-tabs-slot ever changes enough to recreate that element
  // (conditional rendering, HMR during dev, etc.), a one-shot lookup would
  // go stale forever, silently portaling into a detached node. Re-checking
  // on a light interval instead self-heals: no visible cost when nothing
  // changed (isConnected stays true and the state setter is a no-op).
  const [topbarTabsNode, setTopbarTabsNode] = useState(null);
  useEffect(() => {
    const check = () => {
      setTopbarTabsNode((prev) => (
        prev && prev.isConnected ? prev : document.getElementById('ap-topbar-tabs-slot')
      ));
    };
    check();
    const interval = setInterval(check, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const root = scrollRootRef.current;
    const filterEl = filterBarRef.current;
    if (!root || !filterEl) return undefined;

    // Nav's own height no longer counts here — it moved out of this
    // scrollable area entirely (portaled into Topbar's persistent header),
    // so it no longer sits on top of scrolled content the way the sticky
    // filter bar below still does.
    const GAP = 12; // breathing room below the stuck filter bar
    const applyOffset = () => {
      const offset = filterEl.offsetHeight + GAP;
      stuckHeightRef.current = offset;
      root.style.setProperty('--section-scroll-offset', `${offset}px`);
    };

    applyOffset();
    const ro = new ResizeObserver(applyOffset);
    ro.observe(filterEl);
    return () => ro.disconnect();
  }, []);

  const sections = useMemo(() => ([
    { id: 'call-stats', label: 'Performance' },
    canReadVoicemail && { id: 'voicemails', label: 'Voicemails' },
    canReadLeads && { id: 'leads', label: 'Leads', badge: leadsPending },
    canUseFollowUps && { id: 'follow-ups', label: 'Follow-Ups', badge: followUpsPending },
    { id: 'call-log', label: 'Call Log' },
    canReadDispositionForms && { id: 'disposition-forms', label: 'Disposition Forms', badge: pendingDispositionsCount, dotOnly: true },
  ].filter(Boolean)), [
    canReadVoicemail,
    canReadLeads,
    canUseFollowUps,
    canReadDispositionForms,
    leadsPending,
    followUpsPending,
    pendingDispositionsCount,
  ]);

  const filterFromStr = useMemo(() => dateToInputStr(csData.startDate), [csData.startDate]);
  const filterToStr = useMemo(() => dateToInputStr(csData.endDate), [csData.endDate]);
  const followUpsToStr = useMemo(
    () => (csData.activePreset === 'today' ? moment().add(30, 'days').format('YYYY-MM-DD') : filterToStr),
    [csData.activePreset, filterToStr],
  );

  const rangeLabel = useMemo(() => {
    const from = moment(csData.startDate);
    const to = moment(csData.endDate);
    const span = from.isSame(to, 'day')
      ? from.format('D MMM YYYY')
      : `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`;
    const n = Number(csData.totals.total) || 0;
    return `${span} · ${n} call${n === 1 ? '' : 's'}`;
  }, [csData.startDate, csData.endDate, csData.totals.total]);

  const handlePresetChange = useCallback(
    (key) => { if (key) csData.applyPreset(key); },
    [csData.applyPreset],
  );

  const handleRangeChange = useCallback(
    (range) => {
      if (!range?.from) return;
      const end = range.to || range.from;
      csData.setStartDate(range.from);
      csData.setEndDate(end);
      csData.setActivePreset('');
      if (range.to) csData.loadAll(range.from, end);
    },
    [csData.setStartDate, csData.setEndDate, csData.setActivePreset, csData.loadAll],
  );

  return (
    <div ref={scrollRootRef} className="-mt-2 flex h-full min-h-0 flex-col overflow-y-auto rounded-b-2xl bg-card px-2 pb-2 shadow-neu-raised">
      {topbarTabsNode && createPortal(
        <DashboardSectionNav
          items={sections}
          scrollRootRef={scrollRootRef}
          onActiveChange={setActiveSectionId}
          navRef={navElRef}
          stuckHeightRef={stuckHeightRef}
          onBeforeNavigate={onRequestDashboard}
        />,
        topbarTabsNode,
      )}

      <PeriodCallsScatterModal
        show={Boolean(csData.scatterPeriod)}
        onHide={csData.closePeriodScatter}
        periodLabel={csData.scatterPeriod?.label || ''}
        calls={csData.scatterCalls}
        loading={csData.scatterLoading}
        theme={theme}
      />

      {/* Order: date filter → Performance (Analytics) → Quick periods →
          the rest of the sections, per request. */}
      <div ref={filterBarRef} className={`cti-kpi-card sticky top-0 z-[5] -mx-2 mb-3 border-b ${HAIRLINE} bg-card px-5 py-3 shadow-sm`}>
        <FilterBarPill
          activePreset={csData.activePreset}
          startDate={csData.startDate}
          endDate={csData.endDate}
          onPresetChange={handlePresetChange}
          onRangeChange={handleRangeChange}
          onReset={csData.handleReset}
          onRefresh={csData.handleRefresh}
          rangeLabel={rangeLabel}
          extra={
            <>
              {activeSectionId === 'voicemails' && (
                <div ref={setVoicemailsPortalNode} className="flex items-center gap-2" />
              )}
              {activeSectionId === 'leads' && (
                <div ref={setLeadsPortalNode} className="flex items-center gap-2" />
              )}
              {activeSectionId === 'follow-ups' && (
                <div ref={setFollowUpsPortalNode} className="flex items-center gap-2" />
              )}
            </>
          }
        />
      </div>
      <div className="isolate space-y-7 pb-[60vh]">
        {/* Not one of the nav's own tabs (see `sections` above) — the
          * Topbar's separate "Groups" chip scrolls straight here instead
          * (Topbar.tsx's handleGroupsSectionClick), the same way a
          * DashboardSectionNav tab click would. */}
        {canReadCallGroup && (
          <DashboardSection
            id="groups"
            eyebrow="Assignment"
            title="My Groups"
            subtitle="Call groups you're assigned to"
          >
            <AgentGroupsSection agentId={agentId} userData={userData} />
          </DashboardSection>
        )}

        {/* Same as "groups" above — not one of the nav's own tabs, the
          * Topbar's "Campaigns" chip scrolls straight here instead. */}
        {canReadCampaigns && (
          <DashboardSection
            id="campaigns"
            eyebrow="Assignment"
            title="My Campaigns"
            subtitle="Campaigns you're active on"
          >
            <AgentCampaignsSection campaigns={campaigns} userData={userData} />
          </DashboardSection>
        )}

        <DashboardSection
          id="call-stats"
          eyebrow="Analytics"
          title="Performance"
          subtitle="Your own call analytics for the selected date range"
          framed={false}
        >
          <div className="flex flex-col gap-3">
            <CallStatsBody
              agentUsername={userData?.username || userData?.agentData?.agent_name}
              theme={theme}
              showHeading={false}
              showCallLog={false}
              showQuickPeriods={false}
              showFilter={false}
              data={csData}
            />
            <LoggedInAgentBreakTable userData={userData} active={active} />
          </div>
        </DashboardSection>

        <div>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <span className="text-[10px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
              Quick periods
            </span>
            <span className="text-[10px] text-muted-foreground">Fixed — not affected by the filter</span>
          </div>
          <PeriodOverviewRow
            periodCards={csData.periodCards}
            isDark={isDark}
            onChartClick={csData.openPeriodScatter}
            activeKey={csData.scatterPeriod?.key}
            loading={csData.periodLoading}
          />
        </div>

        {canReadVoicemail && (
          <DashboardSection
            id="voicemails"
            eyebrow="Inbox"
            title="Voicemails"
            subtitle="Messages left for you"
          >
            <VoicemailsPanel
              fromDate={filterFromStr}
              toDate={filterToStr}
              filterPortalTarget={activeSectionId === 'voicemails' ? voicemailsPortalNode : null}
            />
          </DashboardSection>
        )}

        {canReadLeads && (
          <DashboardSection
            id="leads"
            eyebrow="Pipeline"
            title="My Leads"
            subtitle="Leads assigned to you"
          >
            <LeadsScreen
              active
              isOutboundEnabled={isOutboundEnabled}
              onCallLead={onCallLead}
              onClose={() => {}}
              onPendingCountChange={setLeadsPending}
              fromDate={filterFromStr}
              toDate={filterToStr}
              statusFilter={leadsStatusFilter}
              onStatusFilterChange={setLeadsStatusFilter}
              searchTerm={leadsSearchTerm}
              onSearchTermChange={setLeadsSearchTerm}
              filterPortalTarget={activeSectionId === 'leads' ? leadsPortalNode : null}
            />
          </DashboardSection>
        )}

        {canUseFollowUps && (
          <DashboardSection
            id="follow-ups"
            eyebrow="Scheduled"
            title="Disposition Follow-Ups"
            subtitle="Callbacks you committed to on a disposition"
          >
            <DispositionFollowUpsPanel
              onCallFollowUp={onCallFollowUp}
              callBlockedReason={followUpCallBlockedReason}
              refreshToken={followUpsRefreshToken}
              onPendingCountChange={setFollowUpsPending}
              fromDate={filterFromStr}
              toDate={followUpsToStr}
              status={followUpsStatus}
              onStatusChange={setFollowUpsStatus}
              search={followUpsSearch}
              onSearchChange={setFollowUpsSearch}
              filterPortalTarget={activeSectionId === 'follow-ups' ? followUpsPortalNode : null}
            />
          </DashboardSection>
        )}

        <DashboardSection
          id="call-log"
          eyebrow="Activity"
          title="Recent Call Log"
          subtitle="Filtered by the same date range as Call Stats, above"
        >
          <CallLogTable
            rows={csData.tableRows}
            loading={csData.recordsLoading}
            onCallNumber={onCallFromLog}
            callBlockedReason={followUpCallBlockedReason}
            serverPaging={csData.callLogPaging}
            onSearchChange={csData.changeCallLogSearch}
            fetchExportRows={csData.fetchCallLogExport}
            exportLabel="Export these calls"
          />
        </DashboardSection>

        {/* DispositionFormsList is the same page the admin Disposition Forms
         * route renders — already agent-scoped (isSelfScopedAgent) to just
         * this agent's own submissions, with its own toolbar/PageHeader, so
         * this section's own title reads a little redundant against it —
         * same tradeoff Performance takes with CallStatsBody above. */}
        {canReadDispositionForms && (
          <DashboardSection
            id="disposition-forms"
            eyebrow="Forms"
            title="Disposition Forms"
            subtitle="Forms shown to you at end-of-call, and what you've submitted"
            framed={false}
          >
            <DispositionFormsList />
          </DashboardSection>
        )}
      </div>
    </div>
  );
}
